import React, { ReactElement, useMemo } from "react";
import { useDomainContext } from "@hooks/useDomainContext";

import VerticalOffer from "@components/shared/offerVertical";
import { useRouter } from "next/router";
import { affiliateOfferZones } from "@lib/shared/config";
import { doesOfferHaveALink, shouldRemoveBuyer } from "@lib/sharedUtils";
import VerticalOfferSkeleton from "@components/shared/offerVertical/skeleton";
import { Buyer } from "@hooks/useForm/types";
import Styles from "../style.module.scss";

export default function OffersColumn({
    winnerBuyer,
    loadingWinnerBuyer,
    onlyShowWinnerBuyer,
    showUnqualifiedOffers,
    template = "default",
}: {
    winnerBuyer: Buyer | null;
    loadingWinnerBuyer: boolean;
    onlyShowWinnerBuyer: boolean;
    showUnqualifiedOffers: boolean;
    template?: "default" | "thankYou";
}): ReactElement {
    const { thankYouOffers, domain, category } = useDomainContext();
    const { query } = useRouter();
    const { eb } = query;

    const filteredOffers = useMemo(
        () => thankYouOffers?.filter((offer) => !shouldRemoveBuyer(eb, offer)),
        [thankYouOffers],
    );
    const stickyOffer = useMemo(() => {
        if (!winnerBuyer && category?.slug === "auto-accident") {
            const walkerAutoAccidentOffer =
                thankYouOffers?.find((offer) =>
                    offer?.slug?.includes("walker"),
                ) ??
                domain.defaultOffers?.find((offer) =>
                    offer?.slug?.includes("walker"),
                );

            if (walkerAutoAccidentOffer) {
                return walkerAutoAccidentOffer;
            }
        }

        if (filteredOffers && filteredOffers.length) {
            const offer = filteredOffers?.find((offer) => offer?.sticky);
            return offer;
        }
        return null;
    }, [
        winnerBuyer,
        category?.slug,
        thankYouOffers,
        domain.defaultOffers,
        filteredOffers,
    ]);

    const finalThankYouOffers = useMemo(() => {
        if (showUnqualifiedOffers) {
            return domain.defaultOffers
                .filter(
                    (offer) =>
                        offer.hiddenAttributes?.unqualifiedOffer === "True",
                )
                .slice(0, 1);
        }
        if (onlyShowWinnerBuyer) {
            let filteredByBuyerId = domain.defaultOffers?.filter((offer) => {
                return offer.buyer?.id === Number(winnerBuyer?.buyerId);
            });

            // If still more than 1 offer after buyer ID filter, filter by integrationUUID
            if (
                filteredByBuyerId &&
                filteredByBuyerId.length > 1 &&
                winnerBuyer?.integration?.uuid
            ) {
                const filteredByIntegrationUUID = filteredByBuyerId.filter(
                    (offer) => {
                        return (
                            offer.hiddenAttributes?.integrationUUID ===
                            winnerBuyer.integration.uuid
                        );
                    },
                );

                // Only use integration UUID filtered results if we found matches
                if (filteredByIntegrationUUID.length > 0) {
                    filteredByBuyerId = filteredByIntegrationUUID;
                }
            }

            return filteredByBuyerId;
        }

        if (filteredOffers && filteredOffers.length && stickyOffer) {
            return filteredOffers.filter((offer) => {
                return offer.id !== stickyOffer.id;
            });
        }
        return filteredOffers;
    }, [
        thankYouOffers,
        onlyShowWinnerBuyer,
        winnerBuyer?.buyerId,
        winnerBuyer?.integration?.uuid,
        domain.defaultOffers,
        showUnqualifiedOffers,
    ]);

    return finalThankYouOffers?.length || stickyOffer ? (
        <>
            <div className="w-full pt-10 md:w-2/4 lg:w-1/3 px-0 md:pl-12">
                {loadingWinnerBuyer ? (
                    <VerticalOfferSkeleton />
                ) : (
                    <>
                        {stickyOffer &&
                        (!onlyShowWinnerBuyer ||
                            (!winnerBuyer &&
                                category?.slug === "auto-accident" &&
                                stickyOffer?.slug?.includes("walker"))) ? (
                            <VerticalOffer
                                zone={affiliateOfferZones.THANK_YOU}
                                template={template || "default"}
                                offer={stickyOffer}
                                displayLinkAndPhoneNumber={doesOfferHaveALink(
                                    stickyOffer,
                                )}
                                customStyle={{
                                    wrapperStyle: Styles.offerColumnWrapper,
                                }}
                            />
                        ) : (
                            <></>
                        )}
                        {finalThankYouOffers?.map((offer, index) => (
                            <VerticalOffer
                                zone={affiliateOfferZones.THANK_YOU}
                                key={offer?.id || index}
                                offer={offer}
                                displayLinkAndPhoneNumber={doesOfferHaveALink(
                                    offer,
                                )}
                                customStyle={{
                                    wrapperStyle: Styles.offerColumnWrapper,
                                }}
                            />
                        ))}
                    </>
                )}
            </div>
        </>
    ) : (
        <></>
    );
}
